// Lo que el panel de jefe de proyecto de la pestaña Obra deduce de los datos de Calypso (Pau 18-10): el semáforo, los
// riesgos, el avance por día, los frentes y lo último que ha pasado. Puro y sin texto (los textos, en i18n): se prueba
// solo. Nada se escribe a mano: todo sale de las piezas, sus fechas y quién las lleva.
import type { EtapaObra, ObraSala, PiezaTodaObra } from './tipos';

export const ZONA_PAU = 'Asia/Bangkok';
/** Sin moverse nada en tantos días, la obra está parada (rojo). */
export const DIAS_PARADA = 3;
/** Días de datos que hacen falta para dar una fecha prevista (antes sería inventada). */
export const DIAS_PARA_PREVER = 5;
const DIA_MS = 86_400_000;

/** Día de Tailandia (AAAA-MM-DD) de un instante. */
export function diaPau(ms: number): string {
  return new Date(ms).toLocaleDateString('en-CA', { timeZone: ZONA_PAU });
}

/** Todas las piezas; con el tablero anterior (sin `todas`), las de la etapa actual. */
export function piezasDe(d: ObraSala): PiezaTodaObra[] {
  return d.todas ?? d.piezas.map((p) => ({ ...p, etapa: d.etapa.numero, frente: p.id.match(/^WS\d+/)?.[0] ?? p.id, hecho_utc: null }));
}

export function etapasDe(d: ObraSala): EtapaObra[] {
  if (d.etapas?.length) return d.etapas;
  return Array.from({ length: d.etapa.de }, (_, i) =>
    i + 1 === d.etapa.numero
      ? { numero: i + 1, nombre: d.etapa.nombre, para: '', total: d.etapa.total, hechas: d.etapa.hechas, en_curso: d.etapa.en_curso }
      : { numero: i + 1, nombre: '', para: '', total: 0, hechas: 0, en_curso: 0 },
  );
}

/** Empezada y sin ningún agente vivo trabajando en ella. */
export function parada(p: PiezaTodaObra): boolean {
  return p.estado === 'en curso' && !p.quien.some((q) => q.vivo);
}

/** El último momento en que algo de la obra se movió (un cierre, un cambio en una rama, una parte que entró). */
export function ultimoMovimiento(piezas: PiezaTodaObra[]): number | null {
  let max: number | null = null;
  for (const p of piezas) {
    const fechas = [p.hecho_utc, p.integrado_ultimo ? `${p.integrado_ultimo}T00:00:00Z` : null, ...p.quien.map((q) => q.ultimo_utc)];
    for (const f of fechas) {
      const ms = f ? Date.parse(f) : NaN;
      if (Number.isFinite(ms) && (max === null || ms > max)) max = ms;
    }
  }
  return max;
}

export type Salud = { color: 'verde' | 'ambar' | 'rojo'; paradas: number; diasQuieta: number | null; cerradas24h: number };

export function salud(d: ObraSala, ahora: number): Salud {
  const piezas = piezasDe(d);
  const paradas = piezas.filter(parada).length;
  const ultimo = ultimoMovimiento(piezas);
  const diasQuieta = ultimo === null ? null : Math.floor((ahora - ultimo) / DIA_MS);
  const cerradas24h = piezas.filter((p) => p.hecho_utc && ahora - Date.parse(p.hecho_utc) <= DIA_MS).length;
  const quedan = piezas.some((p) => p.estado !== 'hecho');
  const color = quedan && (diasQuieta === null || diasQuieta >= DIAS_PARADA) ? 'rojo' : paradas > 0 ? 'ambar' : 'verde';
  return { color, paradas, diasQuieta, cerradas24h };
}

/** Riesgos que se detectan solos, de más a menos grave. */
export type Riesgo =
  | { clave: 'quieta'; dias: number }
  | { clave: 'paradas'; n: number }
  | { clave: 'allow'; n: number }
  | { clave: 'sinEmpezarEtapa'; etapa: number };

export function riesgos(d: ObraSala, ahora: number): Riesgo[] {
  const s = salud(d, ahora);
  const r: Riesgo[] = [];
  if (s.color === 'rojo' && s.diasQuieta !== null) r.push({ clave: 'quieta', dias: s.diasQuieta });
  if (s.paradas > 0) r.push({ clave: 'paradas', n: s.paradas });
  if (d.esperando_allow.length > 0) r.push({ clave: 'allow', n: d.esperando_allow.length });
  const actual = etapasDe(d).find((e) => e.numero === d.etapa.numero);
  if (actual && actual.total > 0 && actual.hechas === 0 && actual.en_curso === 0) r.push({ clave: 'sinEmpezarEtapa', etapa: actual.numero });
  return r;
}

/** Piezas cerradas acumuladas por día de Tailandia en la etapa actual, desde el día antes del primer cierre (al menos
 *  una semana), y la fecha prevista de fin si hay días de datos suficientes. */
export function avance(d: ObraSala, ahora: number): { dias: Array<{ dia: string; hechas: number }>; total: number; prevista: string | null } {
  const etapa = piezasDe(d).filter((p) => p.etapa === d.etapa.numero);
  const cierres = etapa.flatMap((p) => (p.hecho_utc ? [Date.parse(p.hecho_utc)] : [])).filter(Number.isFinite).sort((a, b) => a - b);
  const hoy = diaPau(ahora);
  const desde = Math.min(cierres.length ? cierres[0]! - DIA_MS : ahora, ahora - 6 * DIA_MS);
  const dias: Array<{ dia: string; hechas: number }> = [];
  for (let t = desde; ; t += DIA_MS) {
    const dia = diaPau(t);
    if (dias.at(-1)?.dia !== dia) dias.push({ dia, hechas: cierres.filter((c) => diaPau(c) <= dia).length });
    if (dia >= hoy) break;
  }
  let prevista: string | null = null;
  if (cierres.length) {
    const diasDatos = (ahora - cierres[0]!) / DIA_MS;
    const quedan = etapa.length - cierres.length;
    if (diasDatos >= DIAS_PARA_PREVER && quedan > 0) prevista = diaPau(ahora + (quedan / (cierres.length / diasDatos)) * DIA_MS);
  }
  return { dias, total: etapa.length, prevista };
}

/** Avance de cada frente en toda la épica: primero los que se mueven (más avanzados arriba), luego los quietos. */
export function frentes(d: ObraSala): Array<{ id: string; nombre: string; total: number; hechas: number; enCurso: number }> {
  const nombre = new Map((d.frentes ?? []).map((f) => [f.id, f.nombre]));
  const por = new Map<string, { id: string; nombre: string; total: number; hechas: number; enCurso: number }>();
  for (const p of piezasDe(d)) {
    const f = por.get(p.frente) ?? { id: p.frente, nombre: nombre.get(p.frente) ?? p.frente, total: 0, hechas: 0, enCurso: 0 };
    f.total += 1;
    if (p.estado === 'hecho') f.hechas += 1;
    if (p.estado === 'en curso') f.enCurso += 1;
    por.set(p.frente, f);
  }
  const avanceDe = (f: { total: number; hechas: number; enCurso: number }) => (f.hechas + f.enCurso / 2) / f.total;
  return [...por.values()].sort((a, b) => avanceDe(b) - avanceDe(a));
}

/** Lo último que ha pasado: piezas cerradas y la última subida, de más nueva a más vieja. */
export function ultimos(d: ObraSala, n = 5): Array<{ cuando: string; tipo: 'hecha' | 'subida'; texto: string }> {
  const ev: Array<{ cuando: string; tipo: 'hecha' | 'subida'; texto: string }> = piezasDe(d).flatMap((p) =>
    p.hecho_utc ? [{ cuando: p.hecho_utc, tipo: 'hecha' as const, texto: p.nombre }] : [],
  );
  const s = d.ultima_subida;
  const fs = s?.updatedAt ?? s?.createdAt;
  if (s && fs) ev.push({ cuando: fs, tipo: 'subida', texto: s.displayTitle });
  return ev.sort((a, b) => Date.parse(b.cuando) - Date.parse(a.cuando)).slice(0, n);
}

/** Porcentaje de toda la épica hecho (entero). */
export function porcentaje(d: ObraSala): { hechas: number; total: number; pct: number } {
  const piezas = piezasDe(d);
  const hechas = piezas.filter((p) => p.estado === 'hecho').length;
  return { hechas, total: piezas.length, pct: piezas.length ? Math.round((100 * hechas) / piezas.length) : 0 };
}
